import { useEffect, useState } from 'react';
import { CreditCard, Wallet, Banknote, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { paymentsApi, type RevenueBreakdown } from '../../lib/api/payments';

interface Props {
  standortId?: number;
  daysWindow?: number;
  embedded?: boolean;
}

const METHOD_LABEL: Record<string, string> = {
  card: 'Karte',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Stripe Link',
  samsung_pay: 'Samsung Pay',
  sepa_debit: 'SEPA-Lastschrift',
  klarna: 'Klarna',
  paypal: 'PayPal',
  giropay: 'giropay',
  sofort: 'Sofort',
  ideal: 'iDEAL',
  bancontact: 'Bancontact',
  afterpay_clearpay: 'Afterpay',
  unknown: 'Unbekannt',
};

const METHOD_ICON: Record<string, React.ReactNode> = {
  card: <CreditCard className="h-4 w-4" />,
  apple_pay: <Wallet className="h-4 w-4" />,
  google_pay: <Wallet className="h-4 w-4" />,
  link: <Wallet className="h-4 w-4" />,
  samsung_pay: <Wallet className="h-4 w-4" />,
  sepa_debit: <Banknote className="h-4 w-4" />,
};

function formatEur(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function PaymentRevenueContent({ standortId, daysWindow = 30 }: { standortId?: number; daysWindow?: number }) {
  const [data, setData] = useState<RevenueBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - daysWindow);
    const sinceStr = since.toISOString().slice(0, 10);
    setLoading(true);
    setError(null);
    paymentsApi
      .revenueBreakdown({ standortId, since: sinceStr })
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [standortId, daysWindow]);

  if (loading) return <p className="text-sm text-muted-foreground">Lade…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  const methods = Array.isArray(data?.byMethod) ? data!.byMethod : [];
  if (methods.length === 0) {
    return <p className="text-sm text-muted-foreground">Kein Umsatz im Zeitraum.</p>;
  }

  const total = methods.reduce((acc, m) => acc + Number(m?.volume_cents ?? 0), 0);
  const totalFee = methods.reduce((acc, m) => acc + Number(m?.fee_cents ?? 0), 0);
  const totalNet = methods.reduce((acc, m) => acc + Number(m?.net_cents ?? 0), 0);

  return (
    <div className="space-y-3">
      <div>
        <div className="text-2xl font-bold tabular-nums">{formatEur(total)}</div>
        <div className="text-xs text-muted-foreground">Umsatz letzte {daysWindow} Tage</div>
        {(totalFee > 0 || totalNet > 0) && (
          <div className="mt-1 flex gap-3 text-xs text-muted-foreground tabular-nums">
            <span>Gebühr: <span className="text-foreground">{formatEur(totalFee)}</span></span>
            <span>Netto: <span className="text-foreground">{formatEur(totalNet)}</span></span>
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        {methods.map((m) => {
          const volume = Number(m?.volume_cents ?? 0);
          const fee = Number(m?.fee_cents ?? 0);
          const count = Number(m?.count ?? 0);
          const pct = total > 0 ? (volume / total) * 100 : 0;
          const feePct = volume > 0 && fee > 0 ? (fee / volume) * 100 : 0;
          const icon = METHOD_ICON[m.method_key] ?? <Globe className="h-4 w-4" />;
          const label = METHOD_LABEL[m.method_key] ?? m.method_key;
          return (
            <div key={m.method_key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center gap-2">
                  <span className="text-muted-foreground">{icon}</span>
                  {label}
                </span>
                <span className="tabular-nums">
                  <span className="font-medium">{formatEur(volume)}</span>
                  <span className="text-xs text-muted-foreground ml-2">({count})</span>
                </span>
              </div>
              {fee > 0 && (
                <div className="flex justify-end text-[10px] text-muted-foreground tabular-nums">
                  Gebühr {formatEur(fee)}{feePct > 0 ? ` · ${feePct.toFixed(2)} %` : ''}
                </div>
              )}
              <div className="h-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PaymentRevenueWidget({ standortId, daysWindow = 30, embedded = false }: Props) {
  const content = <PaymentRevenueContent standortId={standortId} daysWindow={daysWindow} />;
  if (embedded) return content;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Umsatz nach Zahlungsart</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
